import { useCallback } from "react";
import { useMemberEntityResolver } from "#app/lib/data/member-directory-provider";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import type { EntityData } from "#app/lib/entity-visuals";
import type { EntityMentionRef } from "./mention-refs";

export function useMentionEntityResolver(): (
  ref: EntityMentionRef,
) => EntityData | undefined {
  const { teamMap, stageMap, workflowMap, issueTypeMap } = useWorkspaceData();
  // Members past the bootstrap roster page resolve through the directory.
  const resolveMember = useMemberEntityResolver();

  return useCallback(
    (ref: EntityMentionRef): EntityData | undefined => {
      switch (ref.kind) {
        case "user": {
          const member = resolveMember(ref.id);
          return member?.kind === "user" ? member : undefined;
        }
        case "team": {
          const team = teamMap.get(ref.id);
          return team ? { kind: "team", id: ref.id, team } : undefined;
        }
        case "stage": {
          const stage = stageMap.get(ref.id);
          return stage
            ? {
                kind: "stage",
                id: ref.id,
                stage,
                workflowId: stage.workflowId ?? ref.workflowId,
              }
            : undefined;
        }
        case "workflow": {
          const workflow = workflowMap.get(ref.id);
          return workflow
            ? { kind: "workflow", id: ref.id, workflow }
            : undefined;
        }
        case "issueType": {
          const issueType = issueTypeMap.get(ref.id);
          return issueType
            ? { kind: "issueType", id: ref.id, issueType }
            : undefined;
        }
        case "issue":
          // No workspace map for issues; the chip's fallback EntityData renders and the hover preview lazy-fetches.
          return undefined;
      }
    },
    [resolveMember, teamMap, stageMap, workflowMap, issueTypeMap],
  );
}